import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { TriangleAlert } from 'lucide-react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/Components/shadcn';
import UIStrings from '@/Utils/UIStrings';
import useAISearchProgress from '../../../api/useAISearchProgress';
import useUpdateSearchParameters from '../../../api/useUpdateSearchParameters';
import { useIdentifiedListContext } from '../utils/IdentifiedListContext';
import FiltersForm from './FiltersTab/FiltersForm';
import RankingCriteriaForm from './RankingCriteriaTab/RankingCriteriaForm';
import RefineSearchHeader from './RefineSearchHeader';
import { fillEveryRankingCriteriaRule, oneFilterMinimumRule, useRefineSearchForm } from './useRefineSearchForm';
import { DefaultRefineSearchFilters, RefineSearchFilters } from '../../../api/useSearchFilters';

type RefineSearchTabs = 'filter' | 'rankingCriteria';
const defaultTab: RefineSearchTabs = 'filter';
export const disableAutoOpenRefineSearchDialogKey = 'disableAutoOpenRefineSearchDialog';

const RefineSearchButton = () => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<RefineSearchTabs>(defaultTab);
    const { data: progressData, isLoading: isProgressDataLoading } = useAISearchProgress();
    const { setRefineSearchFilters } = useIdentifiedListContext();
    const form = useRefineSearchForm();
    const [globalValidationError, setGlobalValidationError] = useState<string | undefined>(undefined);
    const isAiSearching = isProgressDataLoading || progressData?.isInProgress;
    const { trigger } = useUpdateSearchParameters();

    useEffect(() => {
        // oneFilterMinimumRule is not a field level rule, it has keys of an empty string
        // https://github.com/react-hook-form/resolvers/issues/541
        const validationErrors = Object.values(form.formState.errors);
        const nonFieldValidationError = validationErrors.find(
            (x) => x.type === oneFilterMinimumRule || x.type === fillEveryRankingCriteriaRule,
        );

        if (nonFieldValidationError) {
            setGlobalValidationError(nonFieldValidationError.message);
        }
    }, [form.formState.errors]);

    const handleCloseDialog = (e?: React.MouseEvent) => {
        e?.preventDefault();
        setOpen(false);
    };

    const handleAfterCloseDialog = () => {
        setTab(defaultTab);
        form.reset();
        setGlobalValidationError(undefined);
    };

    const handleApplyChange = async (newRefineSearch: RefineSearchFilters) => {
        await trigger({
            jobTitleSearchPeriod: newRefineSearch.jobTitleSearchPeriod,
            jobTitles: newRefineSearch.jobTitles.map((jt) => jt.label) || [],
            companiesSearchPeriod: newRefineSearch.companiesSearchPeriod,
            companies: newRefineSearch.companies.map((c) => c.label) || [],
            locationRadius: newRefineSearch.locationRadius,
            locations:
                newRefineSearch.locations.map((l) => ({
                    placeId: l.placeId,
                    placeType: l.placeType,
                    title: l.title,
                    position: l.position,
                    locality: l.locality,
                    subRegion: l.subRegion,
                    region: l.region,
                    country: l.country,
                    localityName: l.localityName,
                    regionName: l.regionName,
                })) || [],
            keywords: newRefineSearch.keywords.map((k) => k.label) || [],
            minYearsOfExperience:
                newRefineSearch.yearsOfExperience?.[0] ?? DefaultRefineSearchFilters.yearsOfExperience[0],
            maxYearsOfExperience:
                newRefineSearch.yearsOfExperience?.[1] ?? DefaultRefineSearchFilters.yearsOfExperience[1],
            searchCriteria: newRefineSearch.rankingCriteria.map((sc) => ({
                id: sc.id,
                criterion: sc.criterion,
            })),
            includeEmptyExperience:
                newRefineSearch.includeEmptyExperience ?? DefaultRefineSearchFilters.includeEmptyExperience,
            languages: newRefineSearch.languages.map((l) => l.label) || [],
            languageProficiency: newRefineSearch.languageProficiency,
        });

        setRefineSearchFilters(newRefineSearch);
        setOpen(false);
    };

    return (
        <>
            <Button variant="default" disabled={isAiSearching} onClick={() => setOpen(true)}>
                {UIStrings.RefineSearch}
            </Button>
            <Dialog
                open={open}
                onOpenChange={(nextOpen) => {
                    if (!nextOpen) {
                        handleCloseDialog();
                    }
                }}
            >
                <DialogContent
                    showCloseButton={false}
                    className="flex max-h-[90vh] min-h-0 max-w-5xl! flex-col gap-0 overflow-hidden p-5"
                    data-cy="refineSearchDialog"
                    onCloseAutoFocus={handleAfterCloseDialog}
                >
                    <DialogTitle className="sr-only">{UIStrings.RefineSearch}</DialogTitle>
                    <DialogDescription className="sr-only">
                        {UIStrings.AISearch} {UIStrings.RefineSearch}
                    </DialogDescription>
                    <FormProvider {...form}>
                        <form
                            className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden"
                            onSubmit={form.handleSubmit(handleApplyChange)}
                        >
                            <DialogHeader className="shrink-0">
                                <RefineSearchHeader onCancel={handleCloseDialog} />
                            </DialogHeader>
                            <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden">
                                {globalValidationError && (
                                    <div className="border-destructive bg-destructive/10 text-destructive flex shrink-0 items-center gap-3 rounded-lg border px-4 py-3 text-sm">
                                        <TriangleAlert className="h-5 w-5 shrink-0" />
                                        {globalValidationError}
                                    </div>
                                )}
                                <Tabs
                                    value={tab}
                                    onValueChange={(value) => setTab(value as RefineSearchTabs)}
                                    className="mt-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                                >
                                    <TabsList
                                        variant="line-primary"
                                        aria-label="refine search tabs"
                                        className="h-auto shrink-0 justify-start"
                                    >
                                        <TabsTrigger value="filter" aria-label="filter tab">
                                            {UIStrings.Filter}
                                        </TabsTrigger>
                                        <TabsTrigger value="rankingCriteria" aria-label="ranking tab">
                                            {UIStrings.RankingCriteria}
                                        </TabsTrigger>
                                    </TabsList>
                                    <TabsContent
                                        value="filter"
                                        className="min-h-0 w-full flex-1 overflow-y-auto overscroll-contain"
                                    >
                                        <FiltersForm />
                                    </TabsContent>
                                    <TabsContent
                                        value="rankingCriteria"
                                        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
                                    >
                                        <RankingCriteriaForm />
                                    </TabsContent>
                                </Tabs>
                            </div>
                        </form>
                    </FormProvider>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RefineSearchButton;
